import React, { useCallback, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { PiClockClockwise } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Text,
} from "@radix-ui/themes";
import {
  GearIcon,
  ChevronRightIcon,
  DotsVerticalIcon,
  ActivityLogIcon,
  PlusIcon,
  Pencil1Icon,
} from "@radix-ui/react-icons";

import {
  automationConfigurationUris,
  isChannelSupported,
} from "@/features/automation-configuration/AutomationConfiguration.config"; // simulator todo - move to v2
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon"; // simulator todo - move to v2
import { Navbar } from "@/components/shared/navbar/Navbar";
import { AiBrowser } from "@/v2/assets/icons/AiBrowser";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { Rotate3d } from "@/v2/assets/icons/3dRotate";
import { SidebarRight } from "@/v2/assets/icons/SidebarRight";
import { Slack } from "@/v2/assets/icons/Slack";
import { SimulatorChannel } from "@/api/simulator/simulator.model";
import { ConversationHistory } from "@/components/shared/conversationHistory";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { StarsIcon } from "@/v2/assets/icons/StarsIcon";
import { BookmarkIcon } from "@/v2/assets/icons/BookmarkIcon";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/v2/components/dataTabs/tabs";

import { BriefDialogGuard } from "../briefDialog";
import { Instructions } from "../instructions";
import {
  useActivityLog,
} from "../../context/useActivityLog";
import { useDraft } from "../../context/useDraft";
import { ReleaseDrawer } from "../release/ReleaseDrawer";
import { ActivityTable } from "../activityTable";
import { useChat } from "../../context/ChatWrapper";
import { GlobalInstructionDialog } from "../globalInstructionDialog";
import { ImproveChatWrapper, ImproveChat } from "../improveChat";
import { useInstructionsNavigation } from "../../context/InstructionsNavigationContext";
import { ImproveChatResetButton } from "../improveChat/ImproveChat";
import { LastResultButton } from "../LastResultButton";
import { useRightPanelTopbarStore } from "../../rightPanelTopbar.store";

import styles from "./styles.module.scss";
import { StructureCheck } from "@/v2/assets/icons/StructureCheck";
import { DescriptionEditTab } from "./DescriptionEditTab";

function getChannelIconWithContainer(channel: SimulatorChannel) {
  const getIcon = () => {
    switch (channel) {
      case "autonomous_ticket_event":
        return <Ticket03Icon size={16} fill="blue" />;
      case "autonomous_quackchat":
        return <Comment01Icon size={16} fill="orange" />;
      case "copilot":
        return <SidebarRight size={16} fill="teal" />;
      case "slackbot":
        return <Slack size={16} fill="purple" />;
      case "knowledge_hub":
        return <Rotate3d size={16} fill="cyan" />;
      default:
        return null;
    }
  };

  const getBackgroundColor = () => {
    switch (channel) {
      case "autonomous_ticket_event":
        return "#EDF6FF";
      case "autonomous_quackchat":
        return "rgba(255, 145, 1, 0.165)";
      case "copilot":
        return "rgba(2, 192, 150, 0.095)";
      case "slackbot":
        return "#FCEFFC";
      case "knowledge_hub":
        return "rgba(1, 204, 255, 0.114)";
      default:
        return "transparent";
    }
  };

  return (
    <div
      style={{
        width: "24px",
        height: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: getBackgroundColor(),
        borderRadius: "4px",
      }}
    >
      {getIcon()}
    </div>
  );
}

export const RightPanel = ({ hideNavbar }: { hideNavbar?: boolean }) => {
  const navigate = useNavigate();
  const { messages } = useChat();
  const [isBriefOpen, setBriefOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [showActivity, setShowActivity] = useState(false);
  const [isReleaseOpen, setIsReleaseOpen] = useState(false);
  const [isGlobalInstructionOpen, setIsGlobalInstructionOpen] = useState(false);
  const [activeTabState, setActiveTabState] = useState("overview");
  const hasTopbar = !hideNavbar && !showActivity;
  const setHasRightPanelTopbar = useRightPanelTopbarStore(
    (state) => state.setHasRightPanelTopbar
  );

  const { activeTab: contextActiveTab, setActiveTab: setContextActiveTab } =
    useInstructionsNavigation();

  useEffect(() => {
    setHasRightPanelTopbar(hasTopbar);

    return () => {
      setHasRightPanelTopbar(false);
    };
  }, [hasTopbar, setHasRightPanelTopbar]);

  const setActiveTab = useCallback(
    (tab: string) => {
      setActiveTabState(tab);
      setContextActiveTab(tab);
    },
    [setContextActiveTab]
  );

  // Sync context activeTab with local state
  useEffect(() => {
    if (contextActiveTab) {
      setActiveTabState(contextActiveTab);
    }
  }, [contextActiveTab]);


  const {
    simulator,
    topics,
    currentRelease,
    descriptionOverrides,
  } = useSimulator();
  const { selectedTopic } = useChat();


  const { topics: topicsChanges, globalInstructions } = useDraft();

  const { chosenSimulatorId } = useMemo(() => {
    return {
      chosenSimulatorId: simulator?.id,
    };
  }, [simulator]);

  const { activities } = useActivityLog({
    filters: {
      selectedReleaseId: currentRelease?.id || null,
      selectedFilters: selectedTopic?.id
        ? [{ type: "topic", value: selectedTopic.id.toString() }]
        : [],
      sortOrder: "newest",
      searchQuery: "",
    },
  });

  const hasNewAutomationConfiguration = isChannelSupported(simulator?.channel);

  useEffect(() => {
    setShowActivity(false);
  }, [selectedTopic]);

  const goodFeedbackCount = useMemo(() => {
    return activities.filter(
      (a) => a.type === "Session" && a.sessionTags?.includes("Good Feedback")
    ).length;
  }, [activities]);

  const badFeedbackCount = useMemo(() => {
    return activities.filter(
      (a) => a.type === "Session" && a.sessionTags?.includes("Bad Feedback")
    ).length;
  }, [activities]);

  const topicsWithUpdates = useMemo(() => {
    return topicsChanges.filter((t) => t.hasUpdates || t.isNew).length;
  }, [topicsChanges]);

  const instructionsCount: number = useMemo(() => {
    return (
      currentRelease?.topicReleasePolicies
        ?.filter((t) => !selectedTopic?.id || t.topicId === selectedTopic.id)
        .map((t) => t.instructions)
        .flat().length ?? 0
    );
  }, [currentRelease, selectedTopic]);

  const regressionTestsEnabled = useFlag("regressionTestsEnabled");

  return (
    <>
      <div className={cx(styles.configurationPanelContainer)}>
        <div className={styles.container} ref={containerRef}>
          {hasTopbar && (
            <div className={styles.topbar}>
              {/* simulator todo - move icon to icons, move this to other component */}

              <Navbar
                items={[
                  {
                    title: "Training Center",
                    svg: (
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(1, 204, 255, 0.114)",
                          borderRadius: "4px",
                        }}
                      >
                        <AiBrowser size={16} fill="cyan" />
                      </div>
                    ),
                    onClick: () => navigate("/automation-center/training"),
                  },
                  {
                    title: "",
                    svg: (
                      <Flex align="center" gap="2">
                        {simulator?.channel &&
                          getChannelIconWithContainer(simulator.channel)}
                        <Text>{simulator?.name || "AI Agent"}</Text>
                        <Badge size="2" color="blue">
                          v{currentRelease?.version}
                        </Badge>
                        <DropdownMenu
                          items={[
                            {
                              label: "Version History",
                              icon: <PiClockClockwise />,
                              onClick: () =>
                                navigate(
                                  `/automation/${chosenSimulatorId}/versions`
                                ),
                            },
                            {
                              label: "Activity",
                              icon: <ActivityLogIcon />,
                              onClick: () => {
                                setShowActivity(true);
                                navigate(
                                  `/automation/${chosenSimulatorId}/activity`
                                );
                              },
                            },
                            ...(regressionTestsEnabled
                              ? [
                                  {
                                    label: "Regression Tests",
                                    icon: (
                                      <StructureCheck size={16} fill="gray" />
                                    ),
                                    onClick: () =>
                                      navigate(
                                        `/automation/${chosenSimulatorId}/regression-tests`
                                      ),
                                  },
                                ]
                              : []),
                          ]}
                        >
                          <DropdownMenu.Trigger>
                            <IconButton
                              variant="ghost"
                              color="gray"
                              size="1"
                              className={styles.menuButton}
                            >
                              <DotsVerticalIcon
                                style={{ width: "14px", height: "14px" }}
                              />
                            </IconButton>
                          </DropdownMenu.Trigger>
                        </DropdownMenu>
                      </Flex>
                    ),
                  },
                ]}
              />

              <Flex gap="3" align="center">
                {simulator && (
                  <LastResultButton simulatorId={simulator.id.toString()} />
                )}
                {hasNewAutomationConfiguration && chosenSimulatorId && (
                  <Button
                    color="gray"
                    variant="outline"
                    highContrast
                    onClick={() => {
                      if (chosenSimulatorId === null) {
                        return;
                      }
                      const to =
                        automationConfigurationUris.getRootUri(
                          chosenSimulatorId
                        );
                      navigate(to);
                    }}
                  >
                    <GearIcon />
                    Configurations
                  </Button>
                )}

                <Button
                  color="gray"
                  variant="solid"
                  highContrast
                  onClick={() => setIsReleaseOpen(true)}
                >
                  Release Manager
                </Button>
              </Flex>
            </div>
          )}

          <div className={cx(styles.content)}>
            <Box className={styles.contentBody}>
              <>
                {selectedTopic?.id ? (
                  <div className={styles.topicContent}>
                    <ImproveChatWrapper topicId={selectedTopic.id}>
                      <Tabs
                        value={activeTabState}
                        onValueChange={(value: string) => {
                          if (value) {
                            setActiveTab(value);
                          }
                        }}
                        className={styles.tabsRoot}
                      >
                        <Flex
                          justify="center"
                          align="center"
                          gap="4"
                          position="relative"
                        >
                          <TabsList>
                            <TabsTrigger value="overview">
                              <BookmarkIcon />
                              Topic Overview
                            </TabsTrigger>
                            <TabsTrigger
                              value="improve"
                              disabled={messages.length <= 1}
                            >
                              <StarsIcon />
                              Improve
                            </TabsTrigger>
                            <TabsTrigger value="description">
                              <Pencil1Icon />
                              Description
                            </TabsTrigger>
                          </TabsList>
                          {activeTabState === "improve" && (
                            <ImproveChatResetButton />
                          )}
                        </Flex>

                        <TabsContent value="overview">
                          <Instructions
                            topicId={selectedTopic.id}
                            topic={selectedTopic.topic}
                            topics={topics}
                            setBriefOpen={setBriefOpen}
                            channel={simulator?.channel}
                            source="simulator"
                          />
                        </TabsContent>

                        <TabsContent
                          value="improve"
                          className={styles.improveTabContent}
                          forceMount
                        >
                          <ImproveChat />
                        </TabsContent>

                        <TabsContent value="description">
                          <DescriptionEditTab />
                        </TabsContent>
                      </Tabs>
                    </ImproveChatWrapper>
                  </div>
                ) : (
                  <Box>
                    <Flex direction="column" align="center" gap="4">
                      <Box className={styles.iconContainer}>
                        {simulator?.channel && (
                          <SimulatorChannelIcon
                            channel={simulator.channel}
                            size="4"
                          />
                        )}
                      </Box>
                      <Flex direction="column" align="center" gap="2">
                        <Text size="5" weight="bold">
                          {simulator?.name || "AI Agent"}
                        </Text>
                        <Flex align="center" gap="2">
                          <Badge size="2" color="blue">
                            v{currentRelease?.version}
                          </Badge>
                          <Button
                            variant="ghost"
                            color="gray"
                            size="1"
                            onClick={() =>
                              navigate(
                                `/automation/${chosenSimulatorId}/versions`
                              )
                            }
                          >
                            <Flex gap="1" align="center">
                              <PiClockClockwise />
                              Version History
                            </Flex>
                          </Button>
                        </Flex>
                      </Flex>

                      <Box className={styles.globalInstructionsContainer}>
                        <Button
                          variant="outline"
                          color="gray"
                          className={styles.globalInstructionsButton}
                          onClick={() => {
                            setIsGlobalInstructionOpen(true);
                          }}
                        >
                          <Flex
                            align="center"
                            gap="1"
                            style={{ width: "100%" }}
                          >
                            <Flex
                              align="center"
                              justify="center"
                              className={styles.plusIconContainer}
                            >
                              <PlusIcon width={14} height={14} />
                            </Flex>
                            <Text size="2" weight="medium" style={{ flex: 1 }}>
                              Global Instructions
                            </Text>
                            {globalInstructions.pending && (
                              <Badge color="blue" size="1">
                                Has Updates
                              </Badge>
                            )}
                            <ChevronRightIcon width={16} height={16} />
                          </Flex>
                        </Button>
                      </Box>

                      <Box className={styles.versionSummary}>
                        <Flex direction="column" gap="2">
                          <Flex justify="between" align="center">
                            <Text size="2" weight="medium">
                              Version Summary
                            </Text>
                            <Button
                              variant="ghost"
                              color="gray"
                              size="1"
                              onClick={() =>
                                navigate(
                                  `/automation/${chosenSimulatorId}/versions/${currentRelease?.id}`
                                )
                              }
                            >
                              <Flex gap="1" align="center">
                                See All
                                <ChevronRightIcon />
                              </Flex>
                            </Button>
                          </Flex>
                          <Flex direction="column" gap="3">
                            <Flex gap="4">
                              <Flex
                                direction="column"
                                gap="1"
                                className={styles.summaryItem}
                              >
                                <Flex>
                                  <Badge
                                    size="2"
                                    variant="solid"
                                    style={{
                                      backgroundColor: "var(--gray-4)",
                                      color: "var(--gray-12)",
                                    }}
                                  >
                                    {topics?.length}
                                  </Badge>
                                </Flex>
                                <Text size="1" color="gray">
                                  Topics
                                </Text>
                              </Flex>
                              <Flex
                                direction="column"
                                gap="1"
                                className={styles.summaryItem}
                              >
                                <Flex>
                                  <Badge
                                    size="2"
                                    variant="solid"
                                    style={{
                                      backgroundColor: "var(--gray-4)",
                                      color: "var(--gray-12)",
                                    }}
                                  >
                                    {topicsWithUpdates}
                                  </Badge>
                                </Flex>
                                <Text size="1" color="gray">
                                  Topics with Updates
                                </Text>
                              </Flex>
                            </Flex>
                            <Flex gap="4">
                              <Flex
                                direction="column"
                                gap="1"
                                className={styles.summaryItem}
                              >
                                <Flex>
                                  <Badge
                                    size="2"
                                    variant="solid"
                                    style={{
                                      backgroundColor: "var(--gray-4)",
                                      color: "var(--gray-12)",
                                    }}
                                  >
                                    {instructionsCount}
                                  </Badge>
                                </Flex>
                                <Text size="1" color="gray">
                                  Instructions
                                </Text>
                              </Flex>
                              <Flex
                                direction="column"
                                gap="1"
                                className={styles.summaryItem}
                              >
                                <Flex>
                                  <Badge
                                    size="2"
                                    variant="solid"
                                    style={{
                                      backgroundColor: "var(--gray-4)",
                                      color: "var(--gray-12)",
                                    }}
                                  >
                                    {currentRelease?.releaseConversations
                                      ?.length ?? 0}
                                  </Badge>
                                </Flex>
                                <Text size="1" color="gray">
                                  Sessions
                                </Text>
                              </Flex>
                              <Flex
                                direction="column"
                                gap="1"
                                className={styles.summaryItem}
                              >
                                <Flex>
                                  <Badge
                                    size="2"
                                    variant="solid"
                                    style={{
                                      backgroundColor: "var(--green-3)",
                                      color: "var(--green-11)",
                                    }}
                                  >
                                    {goodFeedbackCount}
                                  </Badge>
                                </Flex>
                                <Text size="1" color="gray">
                                  Good
                                </Text>
                              </Flex>
                              <Flex
                                direction="column"
                                gap="1"
                                className={styles.summaryItem}
                              >
                                <Flex>
                                  <Badge
                                    size="2"
                                    variant="solid"
                                    style={{
                                      backgroundColor: "var(--red-3)",
                                      color: "var(--red-11)",
                                    }}
                                  >
                                    {badFeedbackCount}
                                  </Badge>
                                </Flex>
                                <Text size="1" color="gray">
                                  Bad
                                </Text>
                              </Flex>
                            </Flex>
                          </Flex>
                        </Flex>
                      </Box>

                      <Box
                        className={styles.feedbackSection}
                        style={{ width: "100%" }}
                      >
                        <Flex direction="column" gap="2">
                          <Flex justify="between" align="center">
                            <Text size="2" weight="medium">
                              Recent Activity
                            </Text>
                            <Button
                              variant="ghost"
                              color="gray"
                              size="1"
                              className={styles.activityButton}
                              onClick={() => {
                                setShowActivity(true);
                                navigate(
                                  `/automation/${chosenSimulatorId}/activity`
                                );
                              }}
                            >
                              <Flex gap="1" align="center">
                                See All
                                <ChevronRightIcon />
                              </Flex>
                            </Button>
                          </Flex>
                          {currentRelease?.id && (
                            <ActivityTable releaseId={currentRelease?.id} />
                          )}
                        </Flex>
                      </Box>
                    </Flex>
                  </Box>
                )}
              </>
            </Box>
          </div>
        </div>

        <ReleaseDrawer isOpen={isReleaseOpen} setOpen={setIsReleaseOpen} />

        {selectedTopic?.id && (
          <BriefDialogGuard
            open={isBriefOpen}
            openChange={(v) => setBriefOpen(v)}
          />
        )}

        <GlobalInstructionDialog
          open={isGlobalInstructionOpen}
          openChange={setIsGlobalInstructionOpen}
        />

        {selectedConversation && (
          <ConversationHistory
            open={!!selectedConversation}
            onOpenChange={(open) => !open && setSelectedConversation(null)}
            conversation={selectedConversation}
          />
        )}
      </div>
    </>
  );
};
